import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

// Inline utility panel for Portfolio Analysis. Modeled on the Denali AI
// Attachment panel pattern (aiApp/Assistant/contexts/AttachmentContext.jsx)
// but generalized to host arbitrary "tools" selected from a registry. The
// panel is a flex-row sibling of the grid -- it pushes content over rather
// than overlaying it.

const UtilityPanelContext = createContext(null)

export const LAYOUT_PRESETS = Object.freeze({
  sidebar: { contentFlex: 3, panelFlex: 1 },
  split: { contentFlex: 1, panelFlex: 1 },
  focus: { contentFlex: 1, panelFlex: 3 }
})

const VALID_LAYOUTS = Object.keys(LAYOUT_PRESETS)
const DEFAULT_LAYOUT = 'sidebar'

function layoutKey (userId) {
  return `portfolioExplorer.${userId}.utilityPanel.layoutMode`
}

function loadLayoutMode (userId) {
  if (userId == null) return DEFAULT_LAYOUT
  const stored = localStorageHelper.tryLoad(layoutKey(userId))
  return VALID_LAYOUTS.includes(stored) ? stored : DEFAULT_LAYOUT
}

export function UtilityPanelProvider ({ userId, children }) {
  const [activeToolId, setActiveToolId] = useState(null)
  const [toolPayload, setToolPayload] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const [layoutMode, setLayoutModeState] = useState(() => loadLayoutMode(userId))
  // Session-only -- intentionally not persisted. Drag-resize is a quick
  // adjustment, not a setting; the user should always wake up in their
  // chosen preset.
  const [customRatio, setCustomRatio] = useState(null)

  // Tools can push transient metadata up to the panel header (eyebrow,
  // title, subject line) and register their own toolbar actions (e.g. a
  // back arrow). Stored as raw values so a tool re-render replaces them
  // cleanly via the existing setState batching.
  const [headerMeta, setHeaderMetaState] = useState(null)
  const [actions, setActionsState] = useState(null)
  // Generic publish channel for tool->host communication. Tools opt in
  // by publishing an arbitrary descriptor (e.g. `{ kind: 'trend',
  // canAddSeries, addSeries, ... }`); the grid runtime-checks `kind`
  // to decide whether to render tool-specific context menu items.
  // Provider stays tool-agnostic.
  const [toolBridge, setToolBridgeState] = useState(null)

  const openTool = useCallback((toolId, payload = null) => {
    setActiveToolId(toolId)
    setToolPayload(payload)
    setIsOpen(true)
  }, [])

  const closeTool = useCallback(() => {
    setIsOpen(false)
    // Clear after the close transition so the panel body doesn't flash
    // an empty state on the way out. Tools that re-open inherit a fresh
    // payload from the next openTool call.
    setHeaderMetaState(null)
    setActionsState(null)
    setToolBridgeState(null)
  }, [])

  const setLayoutMode = useCallback((mode) => {
    if (!VALID_LAYOUTS.includes(mode)) return
    setLayoutModeState(mode)
    setCustomRatio(null)
    if (userId != null) {
      localStorageHelper.tryStore(layoutKey(userId), mode)
    }
  }, [userId])

  const setHeaderMeta = useCallback((meta) => {
    setHeaderMetaState(meta)
  }, [])

  const registerToolActions = useCallback((node) => {
    setActionsState(node)
  }, [])

  const setToolBridge = useCallback((bridge) => {
    setToolBridgeState(bridge)
  }, [])

  const effectivePreset = customRatio || LAYOUT_PRESETS[layoutMode] || LAYOUT_PRESETS[DEFAULT_LAYOUT]

  const value = useMemo(() => ({
    isOpen,
    activeToolId,
    toolPayload,
    openTool,
    closeTool,
    layoutMode,
    setLayoutMode,
    customRatio,
    setCustomRatio,
    effectivePreset,
    headerMeta,
    setHeaderMeta,
    actions,
    registerToolActions,
    toolBridge,
    setToolBridge
  }), [
    isOpen,
    activeToolId,
    toolPayload,
    openTool,
    closeTool,
    layoutMode,
    setLayoutMode,
    customRatio,
    effectivePreset,
    headerMeta,
    setHeaderMeta,
    actions,
    registerToolActions,
    toolBridge,
    setToolBridge
  ])

  return (
    <UtilityPanelContext.Provider value={value}>
      {children}
    </UtilityPanelContext.Provider>
  )
}

UtilityPanelProvider.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node
}

export function useUtilityPanel () {
  const ctx = useContext(UtilityPanelContext)
  if (!ctx) {
    throw new Error('useUtilityPanel must be used within a UtilityPanelProvider')
  }
  return ctx
}
